import { AppThunkAction } from '../reducers';
import { ErrorType } from '../../models/HttpError';
import { Concept } from '../../models/Entities/Contracts/Concept';
import { NewConcept } from '../../models/Entities/Contracts/NewConcept';
import { ConceptParameters } from '../../models/Entities/Contracts/ConceptParameters';

import * as Services from '../../services/Concept.Services';
import * as Actions from '../actions/Concept.Actions';

type KnownAction =
  | Actions.ConceptGetAllRequestAction
  | Actions.ConceptGetAllReceiveAction
  | Actions.ConceptGetAllFailAction
  | Actions.ConceptGetByIdRequestAction
  | Actions.ConceptGetByIdReceiveAction
  | Actions.ConceptGetByIdFailAction
  | Actions.ConceptAddRequestAction
  | Actions.ConceptAddReceiveAction
  | Actions.ConceptAddFailAction
  | Actions.ConceptUpdateRequestAction
  | Actions.ConceptUpdateReceiveAction
  | Actions.ConceptUpdateFailAction
  | Actions.ConceptDeleteRequestAction
  | Actions.ConceptDeleteReceiveAction
  | Actions.ConceptDeleteFailAction;

const unauthorizedError = (): ErrorType => ({
  ErrorCode: 401,
  ErrorMessage: 'Not authorized',
  Errors: []
});

const getToken = (getState: any): string | undefined => getState()?.oidc?.user?.access_token;

export const actionCreators = {
  GetAllConcepts:
    (parameters?: ConceptParameters): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);

      if (token === undefined) {
        dispatch({
          type: 'CONCEPT_GET_ALL_FAIL_ACTION',
          error: unauthorizedError()
        });
        return;
      }

      dispatch({
        type: 'CONCEPT_GET_ALL_REQUEST_ACTION',
        parameters
      });

      Services.GetAllConcepts(token, parameters)
        .then((concepts: Concept[]) => {
          dispatch({
            type: 'CONCEPT_GET_ALL_RECEIVE_ACTION',
            concepts
          });
        })
        .catch((error: ErrorType) => {
          dispatch({
            type: 'CONCEPT_GET_ALL_FAIL_ACTION',
            error
          });
        });
    },

  GetConceptById:
    (conceptId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);

      if (token === undefined) {
        dispatch({
          type: 'CONCEPT_GET_BY_ID_FAIL_ACTION',
          error: unauthorizedError()
        });
        return;
      }

      dispatch({
        type: 'CONCEPT_GET_BY_ID_REQUEST_ACTION',
        conceptId
      });

      Services.GetConceptById(token, conceptId)
        .then((concept: Concept) => {
          dispatch({
            type: 'CONCEPT_GET_BY_ID_RECEIVE_ACTION',
            concept
          });
        })
        .catch((error: ErrorType) => {
          dispatch({
            type: 'CONCEPT_GET_BY_ID_FAIL_ACTION',
            error
          });
        });
    },

  AddConcept:
    (newConcept: NewConcept): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);

      if (token === undefined) {
        dispatch({
          type: 'CONCEPT_ADD_FAIL_ACTION',
          error: unauthorizedError()
        });
        return;
      }

      dispatch({
        type: 'CONCEPT_ADD_REQUEST_ACTION',
        newConcept
      });

      Services.CreateConcept(token, newConcept)
        .then((concept: Concept) => {
          dispatch({
            type: 'CONCEPT_ADD_RECEIVE_ACTION',
            concept
          });
        })
        .catch((error: ErrorType) => {
          dispatch({
            type: 'CONCEPT_ADD_FAIL_ACTION',
            error
          });
        });
    },

  UpdateConcept:
    (concept: Concept): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);

      if (token === undefined) {
        dispatch({
          type: 'CONCEPT_UPDATE_FAIL_ACTION',
          error: unauthorizedError()
        });
        return;
      }

      dispatch({
        type: 'CONCEPT_UPDATE_REQUEST_ACTION',
        concept
      });

      Services.UpdateConcept(token, concept)
        .then((updated: Concept) => {
          dispatch({
            type: 'CONCEPT_UPDATE_RECEIVE_ACTION',
            concept: updated
          });
        })
        .catch((error: ErrorType) => {
          dispatch({
            type: 'CONCEPT_UPDATE_FAIL_ACTION',
            error
          });
        });
    },

  DeleteConcept:
    (conceptId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);

      if (token === undefined) {
        dispatch({
          type: 'CONCEPT_DELETE_FAIL_ACTION',
          error: unauthorizedError()
        });
        return;
      }

      dispatch({
        type: 'CONCEPT_DELETE_REQUEST_ACTION',
        conceptId
      });

      Services.DeleteConcept(token, conceptId)
        .then(() => {
          dispatch({
            type: 'CONCEPT_DELETE_RECEIVE_ACTION',
            conceptId
          });
        })
        .catch((error: ErrorType) => {
          dispatch({
            type: 'CONCEPT_DELETE_FAIL_ACTION',
            error
          });
        });
    }
};
